/*
 * Copyright (c) 2017 Datarista, Inc.
 */

import { Map, Record, List } from 'immutable';
import { actionTypes } from '../actions/customerPortal';
import { User } from './user';

export const Installation = new Record(
  {
    id: 0,
    orderId: 0,
    installStatus: '',
    processingStatus: '',
    installKey: '',
    apiKey: '',
    installDate: '',
    crmAuthId: 0,
    description: '',
    createdOn: '',
    updatedOn: '',
    installed: false,
    customConfiguration: {}
  },
  'Installation'
);

export const InstallationMeta = new Record(
  {
    name: '',
    description: '',
    keyField: {
      name: '',
      description: '',
      dataType: '',
      status: {
        statusType: '',
        detail: '',
      },
    },
    status: {
      statusType: '',
      detail: '',
    },
    fieldMetas: [
      {
        name: '',
        description: '',
        dataType: '',
        status: {
          statusType: '',
          detail: '',
        },
      },
    ],
  },
  'InstallationMeta'
);

export const InstallationUser = new Record(
  {
    id: 0,
    accountId: 0,
    installationId: 0,
    userId: 0,
    status: 0,
    permissions: '',
    createdBy: 0,
    createdOn: '',
    updatedBy: 0,
    updatedOn: '',
  },
  'InstallationUser'
);

export const AvailableTags = new Record(
  {
    tagField: '',
    tagValue: '',
    tagDescription: '',
  },
  'availableTags'
);

export const UseCases = new Record(
  {
    code: '',
    description: '',
  },
  'useCases'
);

const customerPortalData = new Map({
  installations: new List(),
});

export const MarketoAuth = new Record(
  {
    clientId: '',
    clientSecret: '',
    restUrl: '',
    identityUrl: '',
  },
  'MarketoAuth'
);

export const MarketoConfig = new Record(
  {
    description: '',
    auth: new MarketoAuth(),
  },
  'MarketoConfig'
);

export const EloquaAuth = new Record(
  {
    authUrl: '',
  },
  'EloquaAuth'
);

export const EloquaConfig = new Record(
  {
    description: '',
    auth: new EloquaAuth(),
  },
  'EloquaConfig'
);

const initialState = customerPortalData;

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case actionTypes.GET_INSTALLS_SUCCESS: {
      const { installsList, instanceId } = action;
      const installs = new List(
        installsList.map(installObj => ({
          user: new User(installObj.user),
          installation: new Installation(installObj.installation),
          installationUser: new InstallationUser(installObj.installationUser),
          availableTags: new List(installObj.availableTags.map(tag => new AvailableTags(tag))),
          useCases: new List(installObj.useCases.map(useCase => new UseCases(useCase))),
          platformCode: installObj.platformCode,
          customConfiguration: installObj.installation.customConfiguration
        }))
      );
      let currentInstall = null;
      if (instanceId) {
        currentInstall = installs
          .valueSeq()
          .find(
            install =>
              install && install.installation.get('id').toString() === instanceId.toString()
          );
      }
      return state.merge({ installations: installs, currentInstall });
    }
    case actionTypes.SET_INSTALL_SUCCESS: {
      const { installObj } = action;
      const currentInstall = {
        user: new User(installObj.user),
        installation: new Installation(installObj.installation),
        installationUser: new InstallationUser(installObj.installationUser),
        availableTags: new AvailableTags(installObj.availableTags),
      };
      return state.merge({ currentInstall });
    }
    case actionTypes.GET_INSTALL_USERS_SUCCESS: {
      const { users } = action;
      const userList = new List(
        users.map(installObj => ({
          user: new User(installObj.user),
          installation: new Installation(installObj.installation),
          installationUser: new InstallationUser(installObj.installationUser),
          availableTags: new AvailableTags(installObj.availableTags),
        }))
      );
      return state.merge({ installUsers: userList });
    }
    case actionTypes.GET_INSTALL_META_SUCCESS: {
      const { meta } = action;
      const metaList = new List(meta.map(metaObj => new InstallationMeta(metaObj)));
      return state.merge({ installMeta: metaList });
    }
    case actionTypes.GET_PLATFORM_MARKETO_CONFIG_SUCCESS:
    case actionTypes.UPDATE_PLATFORM_MARKETO_CONFIG_SUCCESS: {
      const { config } = action;
      let auth = {};
      if (config.auth) {
        auth = new MarketoAuth({
          clientId: config.auth.clientId,
          clientSecret: config.auth.clientSecret,
          restUrl: config.auth.restUrl,
          identityUrl: config.auth.identityUrl,
        });
      }

      const marketoAuthConfig = new MarketoConfig({
        auth,
        description: config.description,
      });

      return state.merge({ marketoAuthConfig, currentPlatformType: 'MARKETO' });
    }
    case actionTypes.GET_PLATFORM_ELOQUA_CONFIG_SUCCESS:
    case actionTypes.UPDATE_PLATFORM_ELOQUA_CONFIG_SUCCESS: {
      const { config } = action;
      let auth = {};
      if (config.auth) {
        auth = new EloquaAuth({
          authUrl: config.auth.authUrl,
        });
      }

      const eloquaAuthConfig = new EloquaConfig({
        auth,
        description: config.description,
      });

      return state.merge({ eloquaAuthConfig, currentPlatformType: 'ELOQUA' });
    }
    default: {
      return state;
    }
  }
}
